// src/components/AdminNewsPage.jsx
import { useState, useEffect, useCallback, useRef } from "react";
import {
  ChevronLeft, Plus, Pencil, Trash2, Pin, Eye, EyeOff,
  AlertTriangle, Upload, Youtube, Image, X as XIcon, ChevronDown,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { API, BANNER_ICONS, fmtDate } from "../utils/constants";
import { ROLE_LABELS } from "../utils/permissions";
import PostBannerIcon from "./PostBannerIcon";

const INPUT = {
  width: "100%", background: "rgba(255,255,255,.04)", border: "1px solid var(--border)",
  borderRadius: 7, padding: "10px 12px", fontSize: 13, color: "var(--text)",
  fontFamily: "Inter,sans-serif", outline: "none", boxSizing: "border-box",
};

/* ─── YouTube URL → embed ID ─────────────────────────────────────────────── */
function parseYouTubeId(url) {
  if (!url) return null;
  const patterns = [
    /youtu\.be\/([^?&#]+)/,
    /youtube\.com\/watch\?v=([^&#]+)/,
    /youtube\.com\/embed\/([^?&#]+)/,
    /youtube\.com\/shorts\/([^?&#]+)/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
}

/* ─── Banner media display (used in both editor preview + NewsFeed) ───────── */
export function PostBanner({ post }) {
  const type = post.banner_type || "icon";

  if (type === "image" && post.banner_image) {
    return (
      <div className="toa-pbanner" style={{ padding: 0 }}>
        <img
          src={post.banner_image}
          alt={post.title}
          style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center", display: "block" }}
        />
      </div>
    );
  }

  if (type === "youtube" && post.banner_youtube) {
    const id = parseYouTubeId(post.banner_youtube);
    if (id) {
      return (
        <div className="toa-pbanner" style={{ padding: 0, position: "relative" }}>
          <iframe
            src={`https://www.youtube.com/embed/${id}?rel=0&modestbranding=1`}
            title="Post video"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none", display: "block" }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      );
    }
  }

  // fallback: icon
  return (
    <div className={`toa-pbanner${post.banner_color === "green" ? " toa-pbanner-2" : ""}`}>
      <PostBannerIcon name={post.banner_icon} />
    </div>
  );
}

/* ─── Banner picker sub-component ────────────────────────────────────────── */
function BannerPicker({ form, setForm, INPUT }) {
  const fileRef = useRef(null);
  const [ytInput, setYtInput] = useState(form.banner_youtube || "");
  const [ytError, setYtError] = useState("");
  const [uploading, setUploading] = useState(false);

  const bannerType = form.banner_type || "icon";

  const tabs = [
    { id: "icon",    label: "Icon",    icon: <Image style={{ width: 12, height: 12 }} /> },
    { id: "image",   label: "Image",   icon: <Upload style={{ width: 12, height: 12 }} /> },
    { id: "youtube", label: "YouTube", icon: <Youtube style={{ width: 12, height: 12 }} /> },
  ];

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert("Image must be under 5 MB"); return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = (ev) => {
      setForm(f => ({ ...f, banner_type: "image", banner_image: ev.target.result }));
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const handleYtApply = () => {
    const id = parseYouTubeId(ytInput.trim());
    if (!id) { setYtError("Couldn't find a YouTube video ID in that URL."); return; }
    setYtError("");
    setForm(f => ({ ...f, banner_type: "youtube", banner_youtube: ytInput.trim() }));
  };

  const clearBanner = () => {
    setForm(f => ({ ...f, banner_type: "icon", banner_image: "", banner_youtube: "" }));
    setYtInput("");
    setYtError("");
  };

  // live preview data
  const previewPost = {
    banner_type:    bannerType,
    banner_image:   form.banner_image,
    banner_youtube: form.banner_youtube,
    banner_icon:    form.banner_icon,
    banner_color:   form.banner_color,
    title: "Preview",
  };
  const ytId = bannerType === "youtube" ? parseYouTubeId(form.banner_youtube || "") : null;

  return (
    <div className="toa-sc" style={{ marginBottom: 14 }}>
      <div className="toa-st">Banner Media</div>
      <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 12 }}>

        {/* type tabs */}
        <div style={{ display: "flex", border: "1px solid var(--border)", borderRadius: 7, overflow: "hidden" }}>
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setForm(f => ({ ...f, banner_type: t.id }))}
              style={{
                flex: 1, padding: "7px 4px", fontSize: 11, fontFamily: "'Cinzel',serif",
                cursor: "pointer", border: "none", display: "flex", alignItems: "center",
                justifyContent: "center", gap: 5, transition: ".15s",
                background: bannerType === t.id ? "linear-gradient(135deg,var(--purple),#5a3d8a)" : "transparent",
                color: bannerType === t.id ? "#fff" : "var(--muted)",
              }}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* icon tab */}
        {bannerType === "icon" && (
          <>
            <div>
              <div style={{ fontSize: 11, color: "var(--text2)", marginBottom: 5, fontFamily: "'Cinzel',serif", textTransform: "uppercase", letterSpacing: ".5px" }}>Icon</div>
              <select value={form.banner_icon} onChange={e => setForm(f => ({ ...f, banner_icon: e.target.value }))} style={INPUT}>
                {BANNER_ICONS.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "var(--text2)", marginBottom: 6, fontFamily: "'Cinzel',serif", textTransform: "uppercase", letterSpacing: ".5px" }}>Background</div>
              <div style={{ display: "flex", gap: 8 }}>
                {[
                  { val: "purple", label: "Purple", grad: "linear-gradient(135deg,#5a3a96,#32165e)", border: "rgba(123,94,167,.6)", col: "var(--purpleB)" },
                  { val: "green",  label: "Green",  grad: "linear-gradient(135deg,#1a3a1a,#0a2a0a)", border: "rgba(74,222,128,.4)",   col: "#4ade80" },
                ].map(c => (
                  <button key={c.val} onClick={() => setForm(f => ({ ...f, banner_color: c.val }))} style={{
                    flex: 1, padding: "8px 0", borderRadius: 7, fontSize: 11, fontFamily: "'Cinzel',serif",
                    cursor: "pointer", transition: ".15s",
                    background: form.banner_color === c.val ? c.grad : "rgba(255,255,255,.04)",
                    border: `1px solid ${form.banner_color === c.val ? c.border : "var(--border)"}`,
                    color: form.banner_color === c.val ? c.col : "var(--muted)",
                  }}>{c.label}</button>
                ))}
              </div>
            </div>
          </>
        )}

        {/* image upload tab */}
        {bannerType === "image" && (
          <div>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            {form.banner_image ? (
              <div style={{ position: "relative" }}>
                <img
                  src={form.banner_image}
                  alt="Banner preview"
                  style={{ width: "100%", height: 90, objectFit: "cover", borderRadius: 7, display: "block" }}
                />
                <button
                  onClick={clearBanner}
                  style={{
                    position: "absolute", top: 6, right: 6, width: 24, height: 24,
                    borderRadius: "50%", background: "rgba(0,0,0,.7)", border: "none",
                    color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >
                  <XIcon style={{ width: 12, height: 12 }} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                style={{
                  width: "100%", padding: "22px 12px", border: "2px dashed var(--borderB)",
                  borderRadius: 8, background: "rgba(255,255,255,.02)", cursor: "pointer",
                  display: "flex", flexDirection: "column", alignItems: "center", gap: 7,
                  color: "var(--muted)", fontSize: 12, fontFamily: "'Cinzel',serif", transition: ".15s",
                }}
              >
                <Upload style={{ width: 20, height: 20, opacity: .5 }} />
                {uploading ? "Processing…" : "Click to upload image"}
                <span style={{ fontSize: 10, opacity: .6 }}>PNG, JPG, GIF, WebP · max 5 MB</span>
              </button>
            )}
            {!form.banner_image && (
              <div style={{ marginTop: 8, fontSize: 11, color: "var(--muted)", textAlign: "center" }}>
                Or drag & drop a file above
              </div>
            )}
          </div>
        )}

        {/* youtube tab */}
        {bannerType === "youtube" && (
          <div>
            <div style={{ fontSize: 11, color: "var(--text2)", marginBottom: 5, fontFamily: "'Cinzel',serif", textTransform: "uppercase", letterSpacing: ".5px" }}>YouTube URL</div>
            <div style={{ display: "flex", gap: 6 }}>
              <input
                type="text"
                placeholder="https://youtu.be/... or youtube.com/watch?v=..."
                value={ytInput}
                onChange={e => { setYtInput(e.target.value); setYtError(""); }}
                onKeyDown={e => e.key === "Enter" && handleYtApply()}
                style={{ ...INPUT, flex: 1, padding: "9px 10px" }}
              />
              <button
                onClick={handleYtApply}
                className="toa-btn toa-btn-p"
                style={{ fontSize: 11, padding: "8px 12px", flexShrink: 0 }}
              >
                Apply
              </button>
            </div>
            {ytError && <div style={{ fontSize: 11, color: "#f87171", marginTop: 5 }}>{ytError}</div>}
            {ytId && (
              <div style={{ position: "relative", marginTop: 8 }}>
                <img
                  src={`https://img.youtube.com/vi/${ytId}/mqdefault.jpg`}
                  alt="YouTube thumbnail"
                  style={{ width: "100%", borderRadius: 7, display: "block" }}
                />
                <div style={{
                  position: "absolute", inset: 0, display: "flex", alignItems: "center",
                  justifyContent: "center", background: "rgba(0,0,0,.35)", borderRadius: 7,
                }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: "50%", background: "rgba(255,0,0,.85)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <div style={{ width: 0, height: 0, borderTop: "7px solid transparent", borderBottom: "7px solid transparent", borderLeft: "12px solid #fff", marginLeft: 3 }} />
                  </div>
                </div>
                <button
                  onClick={clearBanner}
                  style={{
                    position: "absolute", top: 6, right: 6, width: 24, height: 24,
                    borderRadius: "50%", background: "rgba(0,0,0,.7)", border: "none",
                    color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >
                  <XIcon style={{ width: 12, height: 12 }} />
                </button>
              </div>
            )}
          </div>
        )}

        {/* live preview (icon mode only — image/youtube are self-evident) */}
        {bannerType === "icon" && (
          <div
            className={`toa-pbanner${form.banner_color === "green" ? " toa-pbanner-2" : ""}`}
            style={{ borderRadius: 8, height: 80, marginTop: 2 }}
          >
            <PostBannerIcon name={form.banner_icon} />
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── delete confirmation ─────────────────────────────────────────────────── */
function DeleteConfirm({ post, onConfirm, onCancel }) {
  return (
    <div className="toa-overlay" onClick={e => e.target === e.currentTarget && onCancel()}>
      <div className="toa-modal" style={{ maxWidth: 380 }}>
        <div style={{ textAlign: "center" }}>
          <div style={{
            width: 52, height: 52, borderRadius: "50%",
            background: "rgba(239,68,68,.12)", border: "1px solid rgba(239,68,68,.3)",
            display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px",
          }}>
            <AlertTriangle style={{ width: 22, height: 22, color: "#ef4444" }} />
          </div>
          <div className="toa-modal-title" style={{ fontSize: 18, marginBottom: 6 }}>Delete Post?</div>
          <div style={{ fontSize: 13, color: "var(--text2)", marginBottom: 4 }}>"{post.title}"</div>
          <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 24 }}>This cannot be undone.</div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="toa-btn toa-btn-ghost" style={{ flex: 1 }} onClick={onCancel}>Cancel</button>
          <button
            className="toa-btn"
            style={{ flex: 1, background: "linear-gradient(135deg,#7f1d1d,#991b1b)", color: "#fca5a5", border: "1px solid rgba(239,68,68,.35)" }}
            onClick={onConfirm}
          >Delete</button>
        </div>
      </div>
    </div>
  );
}

/* ─── post editor ─────────────────────────────────────────────────────────── */
export function PostEditor({ post, onBack, onSaved, showToast }) {
  const { accessToken } = useAuth();
  const isEdit = !!post;

  const parseTags = (raw) => {
    if (!raw) return "";
    if (Array.isArray(raw)) return raw.join(", ");
    try { return JSON.parse(raw).join(", "); } catch { return raw; }
  };

  const [form, setForm] = useState({
    title:          post?.title          ?? "",
    excerpt:        post?.excerpt        ?? "",
    body:           post?.body           ?? "",
    banner_type:    post?.banner_type    ?? "icon",
    banner_icon:    post?.banner_icon    ?? "Sword",
    banner_color:   post?.banner_color   ?? "purple",
    banner_image:   post?.banner_image   ?? "",
    banner_youtube: post?.banner_youtube ?? "",
    tags:           parseTags(post?.tags),
    published:      post?.published      ?? true,
    pinned:         post?.pinned         ?? false,
  });
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(false);

  const set = k => e =>
    setForm(f => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const handleSave = async () => {
    if (!form.title.trim() || !form.excerpt.trim() || !form.body.trim()) {
      showToast("Title, excerpt and body are required."); return;
    }
    setLoading(true);
    try {
      const tags   = form.tags.split(",").map(t => t.trim()).filter(Boolean);
      const url    = isEdit ? `${API}/news/${post.id}` : `${API}/news`;
      const method = isEdit ? "PUT" : "POST";
      const res    = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        credentials: "include",
        body: JSON.stringify({ ...form, tags }),
      });
      const data = await res.json();
      if (!res.ok) { showToast(data.errors?.[0]?.msg || data.error || "Failed."); return; }
      showToast(isEdit ? "Post updated!" : "Post created!");
      onSaved(); onBack();
    } catch { showToast("Network error."); }
    finally { setLoading(false); }
  };

  const tagList = form.tags.split(",").map(t => t.trim()).filter(Boolean);
  const now = new Date();

  return (
    <div className="toa-admin-page">
      <div className="toa-admin-bar">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button className="toa-btn toa-btn-ghost toa-admin-back" onClick={onBack}>
            <ChevronLeft style={{ width: 14, height: 14 }} /> Back
          </button>
          <span className="toa-admin-bar-title">{isEdit ? "Edit Post" : "New Post"}</span>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="toa-btn toa-btn-ghost" style={{ fontSize: 11, padding: "7px 14px" }} onClick={() => setPreview(p => !p)}>
            {preview ? <><EyeOff style={{ width: 13, height: 13 }} /> Edit</> : <><Eye style={{ width: 13, height: 13 }} /> Preview</>}
          </button>
          <button className="toa-btn toa-btn-p" style={{ fontSize: 11, padding: "7px 18px", opacity: loading ? .6 : 1 }} onClick={handleSave} disabled={loading}>
            {loading ? "Saving…" : isEdit ? "Save Changes" : "Publish"}
          </button>
        </div>
      </div>

      <div className="toa-admin-body">
        {preview ? (
          <div>
            <div style={{ marginBottom: 20, padding: "10px 14px", background: "rgba(201,168,76,.06)", border: "1px solid rgba(201,168,76,.2)", borderRadius: 8, fontSize: 12, color: "var(--gold)", fontFamily: "'Cinzel',serif" }}>
              Preview — this is how the post will look on the home page
            </div>
            <article className="toa-post">
              <div className="toa-ph">
                <div className="toa-pdate">
                  <div className="toa-pday">{String(now.getDate()).padStart(2,"0")}</div>
                  <div className="toa-pmon">{now.toLocaleString("en-GB",{month:"short"}).toUpperCase()}</div>
                </div>
                <div>
                  <div className="toa-ptitle">{form.title || "Post Title"}</div>
                  <div className="toa-pinfo">By <span>You</span> · {now.toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"})}</div>
                </div>
              </div>
              <PostBanner post={form} />
              <div className="toa-pbody">
                {tagList.map(t => <span key={t} className="toa-tag">{t}</span>)}
                <p>{form.excerpt || "Excerpt will appear here."}</p>
                {form.body && <div style={{ marginTop: 16, borderTop: "1px solid var(--border)", paddingTop: 16 }} dangerouslySetInnerHTML={{ __html: form.body }} />}
              </div>
            </article>
          </div>
        ) : (
          <div className="toa-editor-grid">
            {/* main fields */}
            <div>
              <div className="toa-field">
                <label>Title *</label>
                <div className="toa-field-wrap">
                  <input type="text" className="no-right-pad" placeholder="Post title…" value={form.title} onChange={set("title")} style={{ fontSize: 15, fontWeight: 600 }} />
                </div>
              </div>
              <div className="toa-field">
                <label>Excerpt — shown on home page *</label>
                <div className="toa-field-wrap">
                  <textarea placeholder="Short summary…" rows={3} maxLength={500} value={form.excerpt} onChange={set("excerpt")} style={{ ...INPUT, resize: "vertical" }} />
                </div>
                <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 3, textAlign: "right" }}>{form.excerpt.length}/500</div>
              </div>
              <div className="toa-field">
                <label>Body — HTML supported *</label>
                <div className="toa-field-wrap">
                  <textarea
                    placeholder={"<p>Full post content. Tags like <strong>, <em>, <ul>, <a href=\"#\"> work.</p>"}
                    rows={14} value={form.body} onChange={set("body")}
                    style={{ ...INPUT, resize: "vertical", fontFamily: "'Courier New',monospace", fontSize: 12, lineHeight: 1.6 }}
                  />
                </div>
              </div>
              <div className="toa-field">
                <label>Tags — comma separated</label>
                <div className="toa-field-wrap">
                  <input type="text" className="no-right-pad" placeholder="Launch, Announcement, Minecraft" value={form.tags} onChange={set("tags")} />
                </div>
                {tagList.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 8 }}>
                    {tagList.map(t => <span key={t} className="toa-tag">{t}</span>)}
                  </div>
                )}
              </div>
            </div>

            {/* sidebar */}
            <div className="toa-editor-sidebar">
              {/* publish toggles */}
              <div className="toa-sc" style={{ marginBottom: 14 }}>
                <div className="toa-st">Publish Settings</div>
                <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 14 }}>
                  {[
                    { key: "published", label: "Publish immediately", hint: "Visible to all visitors" },
                    { key: "pinned",    label: "Pin to top",          hint: "Always shown first" },
                  ].map(({ key, label, hint }) => (
                    <label key={key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", gap: 10 }}>
                      <div>
                        <div style={{ fontSize: 13, color: "var(--text)" }}>{label}</div>
                        <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 1 }}>{hint}</div>
                      </div>
                      <div
                        onClick={() => setForm(f => ({ ...f, [key]: !f[key] }))}
                        style={{
                          width: 38, height: 22, borderRadius: 11, flexShrink: 0,
                          background: form[key] ? "var(--purple)" : "rgba(255,255,255,.08)",
                          border: `1px solid ${form[key] ? "var(--purpleB)" : "var(--border)"}`,
                          position: "relative", cursor: "pointer", transition: ".2s",
                        }}
                      >
                        <div style={{
                          position: "absolute", top: 2, width: 16, height: 16, borderRadius: "50%",
                          background: form[key] ? "#fff" : "var(--muted)",
                          left: form[key] ? 18 : 2, transition: ".2s",
                        }} />
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* banner picker */}
              <BannerPicker form={form} setForm={setForm} INPUT={INPUT} />

              <button className="toa-btn toa-btn-p" style={{ width: "100%", opacity: loading ? .6 : 1 }} onClick={handleSave} disabled={loading}>
                {loading ? "Saving…" : isEdit ? "Save Changes" : "Publish Post"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── News panel (inner) ──────────────────────────────────────────────────── */
export function NewsPanel({ accessToken, showToast, onEditPost }) {
  const [posts,    setPosts]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [toggling, setToggling] = useState(null);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/news/admin/all`, {
        headers: { Authorization: `Bearer ${accessToken}` }, credentials: "include",
      });
      const data = res.ok ? await res.json() : await fetch(`${API}/news/home`).then(r => r.json());
      setPosts(data.posts || []);
    } catch { setPosts([]); }
    finally  { setLoading(false); }
  }, [accessToken]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const handleToggle = async (post, field) => {
    setToggling(post.id);
    try {
      await fetch(`${API}/news/${post.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        credentials: "include",
        body: JSON.stringify({ [field]: !post[field] }),
      });
      await fetchPosts();
    } finally { setToggling(null); }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await fetch(`${API}/news/${deleting.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${accessToken}` }, credentials: "include",
      });
      setDeleting(null); await fetchPosts();
    } catch { showToast("Delete failed."); }
  };

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div className="toa-admin-stats" style={{ flex: 1, marginBottom: 0, marginRight: 16 }}>
          {[
            { label: "Total",     val: posts.length },
            { label: "Published", val: posts.filter(p => p.published).length },
            { label: "Drafts",    val: posts.filter(p => !p.published).length },
            { label: "Pinned",    val: posts.filter(p => p.pinned).length },
          ].map(({ label, val }) => (
            <div key={label} className="toa-sc toa-admin-stat">
              <div className="toa-admin-stat-val">{val}</div>
              <div className="toa-admin-stat-lbl">{label}</div>
            </div>
          ))}
        </div>
        <button className="toa-btn toa-btn-p" style={{ fontSize: 11, padding: "7px 16px", flexShrink: 0 }} onClick={() => onEditPost("new")}>
          <Plus style={{ width: 13, height: 13 }} /> New Post
        </button>
      </div>

      {loading ? (
        <div style={{ color: "var(--muted)", fontSize: 14, padding: "40px 0", textAlign: "center" }}>Loading posts…</div>
      ) : posts.length === 0 ? (
        <div style={{ textAlign: "center", padding: "70px 20px" }}>
          <div style={{ fontSize: 42, marginBottom: 14, opacity: .25 }}>📰</div>
          <div style={{ fontFamily: "'Cinzel',serif", fontSize: 16, color: "var(--text2)", marginBottom: 8 }}>No posts yet</div>
          <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 22 }}>Create your first news post to get started.</div>
          <button className="toa-btn toa-btn-p" onClick={() => onEditPost("new")}>
            <Plus style={{ width: 14, height: 14 }} /> Create First Post
          </button>
        </div>
      ) : (
        <div className="toa-admin-list">
          {posts.map(post => {
            const { day, mon } = fmtDate(post.created_at);
            return (
              <div key={post.id} className="toa-admin-row">
                <div className="toa-admin-date">
                  <div className="toa-pday" style={{ fontSize: 16 }}>{day}</div>
                  <div className="toa-pmon" style={{ fontSize: 9 }}>{mon}</div>
                </div>
                <div className="toa-admin-row-info">
                  <div className="toa-admin-row-title">
                    {post.pinned && <Pin style={{ width: 11, height: 11, marginRight: 5, color: "var(--gold)", verticalAlign: "middle", flexShrink: 0 }} />}
                    {post.title}
                  </div>
                  <div className="toa-admin-row-meta">
                    By <span style={{ color: "var(--purpleB)" }}>{post.author}</span>
                    {" · "}
                    {new Date(post.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                  </div>
                </div>
                <span className="toa-admin-status" style={{
                  background: post.published ? "rgba(74,222,128,.1)" : "rgba(100,100,100,.1)",
                  border: `1px solid ${post.published ? "rgba(74,222,128,.25)" : "rgba(100,100,100,.2)"}`,
                  color: post.published ? "#4ade80" : "var(--muted)",
                }}>
                  {post.published ? "Live" : "Draft"}
                </span>
                <div className="toa-admin-actions">
                  <button title={post.published ? "Unpublish" : "Publish"} onClick={() => handleToggle(post, "published")} disabled={toggling === post.id} className="toa-admin-action" style={{ color: post.published ? "#4ade80" : "var(--muted)" }}>
                    {post.published ? <Eye style={{ width: 13, height: 13 }} /> : <EyeOff style={{ width: 13, height: 13 }} />}
                  </button>
                  <button title={post.pinned ? "Unpin" : "Pin to top"} onClick={() => handleToggle(post, "pinned")} disabled={toggling === post.id} className="toa-admin-action" style={{ color: post.pinned ? "var(--gold)" : "var(--muted)" }}>
                    <Pin style={{ width: 13, height: 13 }} />
                  </button>
                  <button title="Edit post" onClick={async () => {
                    try {
                      // Use the admin route (not the public /news/:slug route) so
                      // drafts — which the public route hides — load correctly.
                      const res  = await fetch(`${API}/news/admin/${post.id}`, {
                        headers: { Authorization: `Bearer ${accessToken}` }, credentials: "include",
                      });
                      if (!res.ok) { showToast("Could not load full post — showing partial data."); onEditPost(post); return; }
                      const data = await res.json();
                      onEditPost(data.post || post);
                    } catch { showToast("Network error loading post."); onEditPost(post); }
                  }} className="toa-admin-action" style={{ color: "var(--purpleB)" }}>
                    <Pencil style={{ width: 13, height: 13 }} />
                  </button>
                  <button title="Delete post" onClick={() => setDeleting(post)} className="toa-admin-action toa-admin-action-del">
                    <Trash2 style={{ width: 13, height: 13 }} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {deleting && <DeleteConfirm post={deleting} onConfirm={handleDelete} onCancel={() => setDeleting(null)} />}
    </>
  );
}

/* ─── Staff panel ─────────────────────────────────────────────────────────── */
export function StaffPanel({ accessToken }) {
  const [staff,   setStaff]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  useEffect(() => {
    fetch(`${API}/admin/staff`, {
      headers: { Authorization: `Bearer ${accessToken}` }, credentials: "include",
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => setStaff(d.staff || d.users || []))
      .catch(() => setError("Could not load staff. Check the /admin/staff endpoint."))
      .finally(() => setLoading(false));
  }, [accessToken]);

  const admins = staff.filter(u => u.role === "admin");
  const mods   = staff.filter(u => u.role !== "admin");

  const RolePill = ({ role }) => {
    const s = role === "admin"
      ? { bg: "rgba(201,168,76,.12)", border: "rgba(201,168,76,.3)",  color: "var(--gold)" }
      : { bg: "rgba(123,94,167,.12)", border: "rgba(123,94,167,.3)",  color: "var(--purpleB)" };
    return (
      <span style={{ fontSize: 10, padding: "2px 9px", borderRadius: 10, background: s.bg, border: `1px solid ${s.border}`, color: s.color, fontFamily: "'Cinzel',serif", letterSpacing: 1, textTransform: "uppercase", flexShrink: 0 }}>
        {ROLE_LABELS[role] || role}
      </span>
    );
  };

  const StaffRow = ({ user }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: "rgba(255,255,255,.02)", border: "1px solid var(--border)", borderRadius: 8, marginBottom: 6 }}>
      <div style={{ width: 36, height: 36, borderRadius: "50%", background: "linear-gradient(135deg,var(--purple),#5a3d8a)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <span style={{ fontFamily: "'Cinzel',serif", fontSize: 14, color: "#fff", fontWeight: 700 }}>{(user.username || "?")[0].toUpperCase()}</span>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "'Cinzel',serif", fontSize: 13, color: "var(--text)", fontWeight: 600 }}>{user.username}</div>
        <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.email}</div>
      </div>
      <RolePill role={user.role} />
    </div>
  );

  if (loading) return <div style={{ color: "var(--muted)", fontSize: 13, padding: "40px 0", textAlign: "center" }}>Loading staff…</div>;
  if (error)   return <div style={{ color: "#f87171", fontSize: 13, padding: "20px 0" }}>{error}</div>;

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 28 }}>
        {[
          { label: "Admins",     val: admins.length, color: "var(--gold)" },
          { label: "Other Staff", val: mods.length,   color: "var(--purpleB)" },
        ].map(s => (
          <div key={s.label} style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 10, padding: "18px 20px" }}>
            <div style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: 26, color: s.color, fontWeight: 900, lineHeight: 1 }}>{s.val}</div>
            <div style={{ fontSize: 11, color: "var(--muted)", fontFamily: "'Cinzel',serif", marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {admins.length > 0 && (
        <>
          <div style={{ fontFamily: "'Cinzel',serif", fontSize: 10, letterSpacing: 4, color: "var(--gold)", textTransform: "uppercase", marginBottom: 10 }}>Admins</div>
          {admins.map(u => <StaffRow key={u.id} user={u} />)}
        </>
      )}
      {mods.length > 0 && (
        <>
          <div style={{ fontFamily: "'Cinzel',serif", fontSize: 10, letterSpacing: 4, color: "var(--purpleB)", textTransform: "uppercase", margin: "20px 0 10px" }}>Other Staff</div>
          {mods.map(u => <StaffRow key={u.id} user={u} />)}
        </>
      )}
      {staff.length === 0 && (
        <div style={{ textAlign: "center", padding: "40px 0", color: "var(--muted)", fontSize: 13 }}>No staff found.</div>
      )}
    </>
  );
}

/* ─── Members panel ───────────────────────────────────────────────────────── */
function memberCountryName(code) {
  if (!code) return "Unknown";
  try { return new Intl.DisplayNames(["en"], { type: "region" }).of(String(code).toUpperCase()) || code; }
  catch { return String(code).toUpperCase(); }
}
function memberCountryFlag(code) {
  const c = String(code || "").toUpperCase();
  if (!/^[A-Z]{2}$/.test(c)) return "🌐";
  return String.fromCodePoint(...[...c].map(ch => 127397 + ch.charCodeAt(0)));
}

export function MembersPanel({ accessToken }) {
  const { authUser } = useAuth();
  const isOwner = !!authUser?.is_owner;
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");
  const [query,   setQuery]   = useState("");
  const [roleDrafts, setRoleDrafts] = useState({});
  const [roleSaving, setRoleSaving] = useState(null);
  const [roleNotice, setRoleNotice] = useState({});
  const [openRoleMenu, setOpenRoleMenu] = useState(null);

  useEffect(() => {
    fetch(`${API}/admin/members`, {
      headers: { Authorization: `Bearer ${accessToken}` }, credentials: "include",
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => setMembers(d.members || d.users || []))
      .catch(() => setError("Could not load members. Check the /admin/members endpoint."))
      .finally(() => setLoading(false));
  }, [accessToken]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? members.filter(m => {
        const code = m.last_country_code || m.signup_country_code || "";
        return m.username?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q) || code.toLowerCase().includes(q) || memberCountryName(code).toLowerCase().includes(q);
      })
    : members;

  const saveRole = async (member) => {
    const role = roleDrafts[member.id] ?? member.role ?? "member";
    if (role === member.role) return;
    if (!isOwner && (role === "admin" || member.role === "admin")) return;

    setRoleSaving(member.id);
    setRoleNotice(n => ({ ...n, [member.id]: "" }));
    try {
      const res = await fetch(`${API}/admin/users/${member.id}/role`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ role }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = data.error || data.errors?.[0]?.msg || "Could not save role.";
        setRoleNotice(n => ({ ...n, [member.id]: msg }));
        return;
      }
      setMembers(list => list.map(m => m.id === member.id ? { ...m, role } : m));
      setOpenRoleMenu(null);
      setRoleDrafts(d => { const next = { ...d }; delete next[member.id]; return next; });
      setRoleNotice(n => ({ ...n, [member.id]: "Saved" }));
      setTimeout(() => setRoleNotice(n => ({ ...n, [member.id]: "" })), 1800);
    } catch {
      setRoleNotice(n => ({ ...n, [member.id]: "Network error." }));
    } finally {
      setRoleSaving(null);
    }
  };

  return (
    <>
      {/* Search bar */}
      <div style={{ position: "relative", marginBottom: 18 }}>
        <svg style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", width: 14, height: 14, color: "var(--muted)", pointerEvents: "none" }}
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
        </svg>
        <input
          type="text" value={query} onChange={e => setQuery(e.target.value)}
          placeholder="Search username, email or country…"
          style={{
            width: "100%", background: "rgba(255,255,255,.04)",
            border: "1px solid var(--border)", borderRadius: 8,
            padding: "10px 12px 10px 34px", fontSize: 13, color: "var(--text)",
            fontFamily: "Inter,sans-serif", outline: "none", boxSizing: "border-box", transition: ".15s",
          }}
          onFocus={e => e.target.style.borderColor = "var(--borderB)"}
          onBlur={e =>  e.target.style.borderColor = "var(--border)"}
        />
      </div>

      {loading ? (
        <div style={{ color: "var(--muted)", fontSize: 13, padding: "40px 0", textAlign: "center" }}>Loading members…</div>
      ) : error ? (
        <div style={{ color: "#f87171", fontSize: 13 }}>{error}</div>
      ) : (
        <>
          <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 12, fontFamily: "'Cinzel',serif" }}>
            {filtered.length} {filtered.length === 1 ? "member" : "members"}{q ? " found" : " registered"}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {filtered.map(m => (
              <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 14px", background: "rgba(255,255,255,.02)", border: "1px solid var(--border)", borderRadius: 8 }}>
                <div style={{ width: 30, height: 30, borderRadius: "50%", background: "rgba(123,94,167,.18)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <span style={{ fontFamily: "'Cinzel',serif", fontSize: 12, color: "var(--purpleB)", fontWeight: 700 }}>{(m.username || "?")[0].toUpperCase()}</span>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: "var(--text)", fontWeight: 600, fontFamily: "'Cinzel',serif" }}>{m.username}</div>
                  <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.email}</div>
                </div>
                <span className="toa-admin-country-pill" title={m.last_country_code ? "Most recent login country" : "Signup country"}>
                  {memberCountryFlag(m.last_country_code || m.signup_country_code)} {memberCountryName(m.last_country_code || m.signup_country_code)}
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                  <div style={{ position: "relative", minWidth: 170 }}>
                    <button
                      type="button"
                      onClick={() => {
                        if (!isOwner && m.role === "admin") return;
                        setOpenRoleMenu(openRoleMenu === m.id ? null : m.id);
                      }}
                      disabled={!isOwner && m.role === "admin"}
                      title={!isOwner && m.role === "admin" ? "Only the site owner can change Administrator roles." : undefined}
                      style={{
                        width: "100%", background: "rgba(255,255,255,.04)",
                        border: `1px solid ${openRoleMenu === m.id ? "var(--borderB)" : "var(--border)"}`,
                        color: "var(--text2)", borderRadius: 6, padding: "6px 8px",
                        fontSize: 10, fontFamily: "'Cinzel',serif",
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        gap: 8, cursor: (!isOwner && m.role === "admin") ? "not-allowed" : "pointer",
                        opacity: (!isOwner && m.role === "admin") ? .6 : 1,
                      }}
                    >
                      <span>{ROLE_LABELS[roleDrafts[m.id] ?? m.role ?? "member"] || (roleDrafts[m.id] ?? m.role ?? "member")}</span>
                      <ChevronDown style={{ width: 12, height: 12, transform: openRoleMenu === m.id ? "rotate(180deg)" : "none", transition: ".15s" }} />
                    </button>
                    {openRoleMenu === m.id && (
                      <div style={{
                        position: "absolute", top: "calc(100% + 5px)", left: 0, right: 0,
                        zIndex: 50, background: "#13102a", border: "1px solid var(--borderB)",
                        borderRadius: 7, padding: 4, boxShadow: "0 12px 30px rgba(0,0,0,.45)",
                      }}>
                        {[
                          ["member", "Member"],
                          ["moderator", "Moderator"],
                          ["minecraft_moderator", "Minecraft Moderator"],
                          ["apparel_manager", "TOA Apparel Manager"],
                          ["marketer", "Marketer"],
                          ...(isOwner ? [["admin", "Administrator"]] : []),
                        ].map(([value, label]) => {
                          const active = (roleDrafts[m.id] ?? m.role ?? "member") === value;
                          return (
                            <button
                              key={value}
                              type="button"
                              onClick={() => {
                                setRoleDrafts(d => ({ ...d, [m.id]: value }));
                                setRoleNotice(n => ({ ...n, [m.id]: "" }));
                                setOpenRoleMenu(null);
                              }}
                              style={{
                                width: "100%", border: "none", borderRadius: 5, padding: "7px 8px",
                                textAlign: "left", background: active ? "rgba(123,94,167,.18)" : "transparent",
                                color: active ? "var(--purpleB)" : "var(--text2)",
                                fontSize: 10, fontFamily: "'Cinzel',serif", cursor: "pointer",
                              }}
                              onMouseOver={e => { if (!active) e.currentTarget.style.background = "rgba(255,255,255,.05)"; }}
                              onMouseOut={e => { if (!active) e.currentTarget.style.background = "transparent"; }}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <button
                    className="toa-btn toa-btn-p"
                    onClick={() => saveRole(m)}
                    disabled={roleSaving === m.id || (roleDrafts[m.id] ?? m.role ?? "member") === m.role || (!isOwner && m.role === "admin")}
                    style={{ padding: "6px 10px", fontSize: 9, minWidth: 58, opacity: (roleSaving === m.id || (roleDrafts[m.id] ?? m.role ?? "member") === m.role) ? .45 : 1 }}
                  >
                    {roleSaving === m.id ? "Saving…" : "Apply"}
                  </button>
                  {roleNotice[m.id] && (
                    <span style={{ fontSize: 10, color: roleNotice[m.id] === "Saved" ? "#4ade80" : "#f87171", whiteSpace: "nowrap" }}>
                      {roleNotice[m.id]}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 10, color: "var(--muted)", fontFamily: "'Cinzel',serif", flexShrink: 0 }}>
                  {m.created_at ? new Date(m.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : ""}
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <div style={{ textAlign: "center", padding: "30px 0", color: "var(--muted)", fontSize: 13 }}>No members match your search.</div>
            )}
          </div>
        </>
      )}
    </>
  );
}

/* NOTE: The top-level dashboard shell (sidebar, top bar, section switching),
 * NavItem, and the route to Staff / Members now live in AdminDashboard.jsx —
 * that file is the default export used by App.jsx. This file keeps the
 * building blocks (PostBanner, PostEditor, NewsPanel, StaffPanel,
 * MembersPanel) as named exports so AdminDashboard.jsx can compose them. */